import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  App,
  Button,
  Col,
  Descriptions,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Switch,
  Typography,
} from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { Product, ProductVariant } from '../../types/pz';
import { createVariant, updateVariant, type VariantFormValues } from '../../modules/products/api';
import { formatProductDimensions } from '../../utils/productDimensions';
import { formatUnit } from '../../utils/units';
import {
  PACKAGING_BOUNDS,
  POLISH_DECIMAL_INPUT_NUMBER_PROPS,
  packagingPositiveDecimalRule,
  packagingStackLayersRule,
} from '../../utils/transportCostAllocation';
import './ProductVariantModal.css';

const { Text, Title } = Typography;
const CURRENCIES = ['PLN', 'EUR', 'USD'];

type AttributeRow = { name?: string; value?: string };
type VariantEditorValues = Omit<VariantFormValues, 'attributes'> & { attributes: AttributeRow[] };

interface ProductVariantModalProps {
  open: boolean;
  product: Product;
  variant?: ProductVariant | null;
  canEditPurchasePrices: boolean;
  canEditSalePrices: boolean;
  onCancel: () => void;
  onSaved: () => void | Promise<void>;
}

const attributeRows = (variant?: ProductVariant | null): AttributeRow[] => {
  const rows = Object.entries(variant?.attributes || {}).map(([name, value]) => ({
    name,
    value: String(value),
  }));
  return rows.length > 0 ? rows : [{ name: '', value: '' }];
};

export const getProductVariantSaveErrorKey = (code?: string) => {
  if (code === 'PRODUCT_VARIANT_HAS_STOCK') return 'products:variantEditor.stockDeactivateBlocked';
  if (code === 'PRODUCT_VARIANT_DUPLICATE_ATTRIBUTES') return 'products:variantEditor.duplicate';
  return undefined;
};

const ProductVariantModal: React.FC<ProductVariantModalProps> = ({
  open,
  product,
  variant,
  canEditPurchasePrices,
  canEditSalePrices,
  onCancel,
  onSaved,
}) => {
  const { t } = useTranslation(['products', 'common']);
  const { message } = App.useApp();
  const [form] = Form.useForm<VariantEditorValues>();
  const [saving, setSaving] = useState(false);
  const watchedRows = Form.useWatch('attributes', form);

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    form.setFieldsValue({
      attributes: attributeRows(variant),
      ean: variant?.eanOverride ?? undefined,
      netWeight: variant?.netWeightOverride ?? undefined,
      grossWeight: variant?.grossWeightOverride ?? undefined,
      piecesPerPackage: variant?.piecesPerPackageOverride ?? undefined,
      packageLength: variant?.packageLengthOverride ?? undefined,
      packageWidth: variant?.packageWidthOverride ?? undefined,
      packageStackLayers: variant?.packageStackLayersOverride ?? undefined,
      ...(canEditPurchasePrices ? {
        defaultPurchasePrice: variant?.defaultPurchasePriceOverride ?? undefined,
        defaultPurchaseCurrency: variant?.defaultPurchaseCurrencyOverride || 'USD',
      } : {}),
      ...(canEditSalePrices ? {
        defaultSalePrice: variant?.defaultSalePriceOverride ?? undefined,
        defaultSaleCurrency: variant?.defaultSaleCurrencyOverride || 'PLN',
      } : {}),
      isActive: variant?.isActive !== false,
    });
  }, [canEditPurchasePrices, canEditSalePrices, form, open, variant]);

  const generatedName = useMemo(() => [...(watchedRows || [])]
    .filter((row) => row?.name?.trim() && row?.value?.trim())
    .sort((left, right) => left.name!.trim().localeCompare(right.name!.trim(), 'pl'))
    .map((row) => row.value!.trim())
    .filter(Boolean)
    .join(' · '), [watchedRows]);

  const validateAttributeRows = (_: unknown, value?: AttributeRow[]) => {
    const complete = (value || []).filter((row) => row?.name?.trim() && row?.value?.trim());
    if (complete.length === 0 || complete.length !== (value || []).length) {
      return Promise.reject(new Error(t('products:variantEditor.attributeRequired')));
    }
    const names = complete.map((row) => row.name!.trim().toLocaleLowerCase('pl'));
    if (new Set(names).size !== names.length) {
      return Promise.reject(new Error(t('products:variantEditor.duplicateAttribute')));
    }
    return Promise.resolve();
  };

  const handleSave = async () => {
    let values: VariantEditorValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    const attributes = Object.fromEntries(values.attributes.map((row) => [
      row.name!.trim(),
      row.value!.trim(),
    ]));
    const payload: VariantFormValues = { ...values, attributes };

    setSaving(true);
    try {
      if (variant) {
        await updateVariant(product.id, variant.id, payload);
        message.success(t('products:success.variantUpdated'));
      } else {
        await createVariant(product.id, payload);
        message.success(t('products:success.variantCreated'));
      }
      await onSaved();
      form.resetFields();
    } catch (error: any) {
      const code = error?.response?.data?.code;
      const errorKey = getProductVariantSaveErrorKey(code);
      if (errorKey) {
        message.error(t(errorKey));
      } else {
        message.error(error?.response?.data?.message || t('products:error.saveVariantFailed'));
      }
    } finally {
      setSaving(false);
    }
  };

  const dimensions = formatProductDimensions(product) || '—';

  return (
    <Modal
      width={820}
      open={open}
      title={variant ? t('products:variantEditor.editTitle') : t('products:variantEditor.createTitle')}
      okText={t('common:save')}
      cancelText={t('common:cancel')}
      confirmLoading={saving}
      onOk={handleSave}
      onCancel={onCancel}
      destroyOnHidden
    >
      <Form form={form} layout="vertical" className="product-variant-modal">
        <section className="product-variant-modal__section product-variant-modal__inherited">
          <Title level={5}>{t('products:variantEditor.inheritedTitle')}</Title>
          <Text type="secondary">{t('products:variantEditor.inheritedHint')}</Text>
          <Descriptions size="small" column={{ xs: 1, sm: 2 }}>
            <Descriptions.Item label={t('products:fields.name')}>{product.name}</Descriptions.Item>
            <Descriptions.Item label={t('products:fields.category')}>
              {product.productCategoryName || t('products:productCategory.none')}
            </Descriptions.Item>
            <Descriptions.Item label={t('products:fields.baseUnit')}>{formatUnit(product.baseUnit)}</Descriptions.Item>
            <Descriptions.Item label={t('products:fields.dimensions')}>{dimensions}</Descriptions.Item>
          </Descriptions>
        </section>

        <section className="product-variant-modal__section">
          <Title level={5}>{t('products:variantEditor.attributesTitle')}</Title>
          <Text type="secondary">{t('products:variantEditor.attributesHint')}</Text>
          <Form.List name="attributes" rules={[{ validator: validateAttributeRows }]}>
              {(fields, { add, remove }, { errors }) => (
                <Space direction="vertical" className="product-variant-modal__attributes" size={8}>
                  {fields.map((field) => (
                    <Space.Compact key={field.key} block>
                      <Form.Item name={[field.name, 'name']} noStyle>
                        <Input
                          maxLength={80}
                          aria-label={t('products:variantEditor.attributeName')}
                          placeholder={t('products:variantEditor.attributeNamePlaceholder')}
                        />
                      </Form.Item>
                      <Form.Item name={[field.name, 'value']} noStyle>
                        <Input
                          maxLength={160}
                          aria-label={t('products:variantEditor.attributeValue')}
                          placeholder={t('products:variantEditor.attributeValuePlaceholder')}
                        />
                      </Form.Item>
                      <Button
                        danger
                        icon={<DeleteOutlined />}
                        aria-label={t('common:delete')}
                        onClick={() => remove(field.name)}
                      />
                    </Space.Compact>
                  ))}
                  <Button type="dashed" icon={<PlusOutlined />} onClick={() => add({ name: '', value: '' })}>
                    {t('products:variantEditor.addAttribute')}
                  </Button>
                  <Form.ErrorList errors={errors} />
                </Space>
              )}
          </Form.List>
          {generatedName && (
            <Alert type="info" showIcon message={t('products:variantEditor.generatedName', { name: generatedName })} />
          )}
        </section>

        <section className="product-variant-modal__section">
          <Title level={5}>{t('products:variantEditor.overridesTitle')}</Title>
          <Text type="secondary">{t('products:variantEditor.overridesHint')}</Text>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label={t('products:fields.ean')} name="ean">
                <Input maxLength={64} placeholder={product.ean || undefined} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label={t('products:variantFields.isActive')} name="isActive" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label={t('products:fields.netWeight')} name="netWeight">
                <InputNumber min={0} step={0.001} placeholder={product.netWeight?.toString()} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label={t('products:fields.grossWeight')} name="grossWeight">
                <InputNumber min={0} step={0.001} placeholder={product.grossWeight?.toString()} />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">{t('products:fields.packagingGroup')}</Divider>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label={t('products:fields.piecesPerPackage')}
                name="piecesPerPackage"
                rules={[packagingPositiveDecimalRule(t, PACKAGING_BOUNDS.maxPiecesPerPackage)]}
              >
                <InputNumber {...POLISH_DECIMAL_INPUT_NUMBER_PROPS} placeholder={product.piecesPerPackage?.toString()} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label={t('products:fields.packageLength')}
                name="packageLength"
                rules={[packagingPositiveDecimalRule(t, PACKAGING_BOUNDS.maxPackageDimensionMm)]}
              >
                <InputNumber {...POLISH_DECIMAL_INPUT_NUMBER_PROPS} placeholder={product.packageLength?.toString()} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label={t('products:fields.packageWidth')}
                name="packageWidth"
                rules={[packagingPositiveDecimalRule(t, PACKAGING_BOUNDS.maxPackageDimensionMm)]}
              >
                <InputNumber {...POLISH_DECIMAL_INPUT_NUMBER_PROPS} placeholder={product.packageWidth?.toString()} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label={t('products:fields.packageStackLayers')}
                name="packageStackLayers"
                rules={[packagingStackLayersRule(t, PACKAGING_BOUNDS.minStackLayers, PACKAGING_BOUNDS.maxStackLayers)]}
              >
                <InputNumber precision={0} placeholder={product.packageStackLayers?.toString()} />
              </Form.Item>
            </Col>
          </Row>

          {(canEditPurchasePrices || canEditSalePrices) && <Divider orientation="left">{t('products:sections.pricing')}</Divider>}
          <Row gutter={16}>
            {canEditPurchasePrices && (
              <>
                <Col xs={24} md={12}>
                  <Form.Item label={t('products:fields.defaultPurchasePrice')} name="defaultPurchasePrice">
                    <InputNumber min={0} step={0.01} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label={t('products:fields.defaultPurchaseCurrency')} name="defaultPurchaseCurrency">
                    <Select options={CURRENCIES.map((currency) => ({ value: currency, label: currency }))} />
                  </Form.Item>
                </Col>
              </>
            )}
            {canEditSalePrices && (
              <>
                <Col xs={24} md={12}>
                  <Form.Item label={t('products:fields.defaultSalePrice')} name="defaultSalePrice">
                    <InputNumber min={0} step={0.01} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label={t('products:fields.defaultSaleCurrency')} name="defaultSaleCurrency">
                    <Select options={CURRENCIES.map((currency) => ({ value: currency, label: currency }))} />
                  </Form.Item>
                </Col>
              </>
            )}
          </Row>
        </section>
      </Form>
    </Modal>
  );
};

export default ProductVariantModal;
